import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/authContext";
import { useIG } from "../lib/igContext";
import {
  Users, Eye, Heart, Calendar, ArrowUpRight, Sparkles, Search, Play,
  Zap, TrendingUp, Bell, Activity
} from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

const ACCENT = "#7C3AED";
const PINK = "#EC4899";

export default function Dashboard() {
  const { user } = useAuth();
  const { accounts } = useIG();
  const [allStats, setAllStats] = useState({});
  const [alerts, setAlerts] = useState([]);
  const [media, setMedia] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [range, setRange] = useState(30);
  const today = new Date().toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });

  useEffect(() => {
    (async () => {
      const m = {};
      for (const a of accounts) {
        try { m[a.id] = await api.igStats(a.id, range); } catch {}
      }
      setAllStats(m);
      try { setAlerts(await api.crmListAlerts()); } catch {}
      try { setMedia(await api.igListMedia()); } catch {}
      try { setContacts(await api.crmListContacts()); } catch {}
    })();
  }, [accounts.length, range]);

  const totalFollowers = accounts.reduce((s, a) => s + (a.followers || 0), 0);
  const totalReach = useMemo(() => {
    let sum = 0;
    Object.values(allStats).forEach(rows => rows.slice(-14).forEach(r => sum += (r.reach || 0)));
    return sum;
  }, [allStats]);
  const avgEng = useMemo(() => {
    const rows = Object.values(allStats).flat();
    return rows.length ? (rows.reduce((s, r) => s + (r.engagement_rate || 0), 0) / rows.length).toFixed(1) : 0;
  }, [allStats]);
  const postsThisMonth = media.filter(m => {
    const d = m.published_at?.slice(0, 7);
    return d === new Date().toISOString().slice(0, 7);
  }).length;
  const new7d = useMemo(() => {
    let s = 0;
    Object.values(allStats).forEach(rows => rows.slice(-7).forEach(r => s += (r.new_followers || 0)));
    return s;
  }, [allStats]);
  const healthScore = Math.min(100, Math.round((accounts.length ? 60 : 0) + Math.min(40, avgEng * 8)));

  // Aggregate series
  const series = useMemo(() => {
    const byDate = {};
    Object.values(allStats).forEach(rows => {
      rows.forEach(r => {
        if (!byDate[r.date]) byDate[r.date] = { date: r.date, reach: 0 };
        byDate[r.date].reach += r.reach || 0;
      });
    });
    return Object.values(byDate).sort((a, b) => a.date.localeCompare(b.date));
  }, [allStats]);

  const topMedia = [...media].sort((a, b) => (b.performance_score || 0) - (a.performance_score || 0)).slice(0, 3);
  const unreadAlerts = alerts.filter(a => !a.is_read).slice(0, 4);

  const pipelineStats = useMemo(() => {
    const stages = { prospect: 0, contacted: 0, negotiating: 0, partner: 0, archived: 0 };
    contacts.forEach(c => { if (stages[c.status] !== undefined) stages[c.status]++; });
    return stages;
  }, [contacts]);

  return (
    <div className="space-y-7 animate-fadeUp" data-testid="dashboard-page">
      {/* HEADER */}
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-4xl font-bold tracking-tight" style={{ color: "var(--text-primary)" }}>
            Bonjour {user?.name?.split(" ")[0] || "👋"} <span>👋</span>
          </h1>
          <p className="text-sm mt-1.5" style={{ color: "var(--text-muted)" }}>
            {today.charAt(0).toUpperCase() + today.slice(1)}
          </p>
        </div>
        <div className="flex items-center gap-4">
          <HealthScore score={healthScore} />
          <Link
            to="/posting-planner"
            data-testid="quick-publish-btn"
            className="cdo-gradient text-white text-sm font-semibold px-5 py-2.5 rounded-xl flex items-center gap-2 glow-purple hover:opacity-95 transition"
          >
            <Sparkles className="w-4 h-4" /> Publication rapide
          </Link>
        </div>
      </header>

      {/* STATS */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="stats-row">
        <StatCard icon={Users} label="Abonnés totaux" value={totalFollowers.toLocaleString("fr-FR")} delta={`+${new7d} cette semaine`} accent={ACCENT} />
        <StatCard icon={Eye} label="Reach 14 jours" value={totalReach.toLocaleString("fr-FR")} delta="vs période préc." accent={PINK} />
        <StatCard icon={Heart} label="Engagement moyen" value={`${avgEng}%`} delta={`${accounts.length} comptes`} accent="#3B82F6" />
        <StatCard icon={Calendar} label="Posts ce mois" value={postsThisMonth} delta={`${media.length} total`} accent="#F59E0B" />
      </section>

      {/* GRAPH */}
      <section className="surface p-6">
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.14em]" style={{ color: "var(--text-muted)" }}>Reach cumulé</div>
            <h3 className="font-display text-xl font-bold mt-0.5" style={{ color: "var(--text-primary)" }}>Évolution sur {range} jours</h3>
          </div>
          <div className="flex rounded-lg border" style={{ borderColor: "var(--border-default)" }}>
            {[30, 60, 90].map(r => (
              <button key={r} onClick={() => setRange(r)} data-testid={`range-${r}`}
                className="px-3 py-1.5 text-xs font-semibold transition"
                style={{
                  background: range === r ? "var(--brand-purple)" : "transparent",
                  color: range === r ? "#FFFFFF" : "var(--text-secondary)",
                  borderRadius: range === 30 ? (r === 30 ? "7px 0 0 7px" : r === 90 ? "0 7px 7px 0" : 0) : 0,
                }}>
                {r}j
              </button>
            ))}
          </div>
        </div>
        {series.length === 0 ? (
          <div className="text-center py-10 text-sm" style={{ color: "var(--text-muted)" }}>
            Aucune donnée — saisis tes premières stats sur Analytics IG.
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={series}>
              <defs>
                <linearGradient id="purpleGrad" x1="0" x2="0" y1="0" y2="1">
                  <stop offset="0%" stopColor={ACCENT} stopOpacity={0.4} />
                  <stop offset="100%" stopColor={ACCENT} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="var(--border-subtle)" vertical={false} />
              <XAxis dataKey="date" tick={{ fill: "var(--text-muted)", fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={(v) => v?.slice(5)} />
              <YAxis tick={{ fill: "var(--text-muted)", fontSize: 11 }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ background: "var(--bg-surface)", border: "1px solid var(--border-default)", borderRadius: 8, fontSize: 12 }} />
              <Area type="monotone" dataKey="reach" stroke={ACCENT} strokeWidth={2.5} fill="url(#purpleGrad)" />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </section>

      {/* QUICK ACTIONS */}
      <section>
        <div className="text-[10px] font-semibold uppercase tracking-[0.14em] mb-3" style={{ color: "var(--text-muted)" }}>Actions rapides</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <QuickAction icon={Zap} title="Générer un hook viral" desc="IA Claude analyse ta niche et te sort 5 hooks calibrés." to="/hooks-bank" color={ACCENT} testid="qa-hook" />
          <QuickAction icon={Play} title="Lancer la routine du jour" desc="Sessions structurées pour booster ton engagement." to="/routine" color={PINK} testid="qa-routine" />
          <QuickAction icon={Search} title="Analyser un concurrent" desc="Spy stratégique avec angles à voler & threat score." to="/competitor-intel" color="#3B82F6" testid="qa-spy" />
        </div>
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* TOP CONTENT */}
        <section className="lg:col-span-2 surface p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-lg font-bold" style={{ color: "var(--text-primary)" }}>Top contenu de la semaine</h3>
            <Link to="/content-table" className="text-xs font-semibold flex items-center gap-1" style={{ color: ACCENT }}>
              Voir tout <ArrowUpRight className="w-3.5 h-3.5" />
            </Link>
          </div>
          {topMedia.length === 0 ? (
            <div className="text-center py-8 text-sm" style={{ color: "var(--text-muted)" }}>Aucun contenu — ajoute via le Tableau Contenus.</div>
          ) : (
            <div className="space-y-2">
              {topMedia.map((m, i) => (
                <div key={m.id} className="flex items-center gap-4 p-3 rounded-lg" style={{ background: "var(--bg-surface-hover)" }} data-testid={`top-media-${i}`}>
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold text-xs"
                    style={{ background: m.performance_score >= 90 ? "linear-gradient(135deg, #EC4899, #F59E0B)" : ACCENT }}>
                    {m.media_type.slice(0, 1).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate" style={{ color: "var(--text-primary)" }}>{m.caption || `${m.media_type} #${i + 1}`}</div>
                    <div className="text-xs font-mono mt-0.5" style={{ color: "var(--text-muted)" }}>
                      {(m.views || 0).toLocaleString("fr-FR")} vues · {m.engagement_rate}% eng · {m.comments} comm
                    </div>
                  </div>
                  {m.performance_score >= 90 && (
                    <span className="text-[10px] font-bold uppercase px-2 py-1 rounded-md text-white" style={{ background: "linear-gradient(135deg, #EC4899, #F59E0B)" }}>
                      VIRAL
                    </span>
                  )}
                  <div className="text-right shrink-0">
                    <div className="text-xs font-mono font-bold" style={{ color: ACCENT }}>{m.performance_score}/100</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* CRM PIPELINE */}
        <section className="surface p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-lg font-bold" style={{ color: "var(--text-primary)" }}>Pipeline CRM</h3>
            <Link to="/crm/pipeline" className="text-xs font-semibold flex items-center gap-1" style={{ color: PINK }}>
              Voir <ArrowUpRight className="w-3.5 h-3.5" />
            </Link>
          </div>
          <div className="space-y-2">
            {[
              { label: "🔍 Prospect", v: pipelineStats.prospect, color: "#3B82F6" },
              { label: "📩 Contacté", v: pipelineStats.contacted, color: "#F59E0B" },
              { label: "💬 En négociation", v: pipelineStats.negotiating, color: "#7C3AED" },
              { label: "🤝 Partenariat", v: pipelineStats.partner, color: "#10B981" },
            ].map(s => (
              <div key={s.label} className="flex items-center justify-between text-sm py-2.5 px-3 rounded-lg" style={{ background: "var(--bg-surface-hover)" }}>
                <span style={{ color: "var(--text-primary)" }}>{s.label}</span>
                <span className="font-mono font-bold" style={{ color: s.color }}>{s.v}</span>
              </div>
            ))}
          </div>
          <Link to="/crm/contacts" data-testid="add-contact-link"
            className="mt-4 w-full block text-center text-xs font-semibold py-2 rounded-lg border transition"
            style={{ color: PINK, borderColor: "var(--border-default)" }}>
            + Ajouter un contact
          </Link>
        </section>
      </div>

      {/* ALERTS */}
      {unreadAlerts.length > 0 && (
        <section className="surface p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Bell className="w-4 h-4" style={{ color: PINK }} />
              <h3 className="font-display text-lg font-bold" style={{ color: "var(--text-primary)" }}>Alertes récentes</h3>
            </div>
            <Link to="/alerts" className="text-xs font-semibold flex items-center gap-1" style={{ color: ACCENT }}>
              Voir tout <ArrowUpRight className="w-3.5 h-3.5" />
            </Link>
          </div>
          <div className="space-y-2">
            {unreadAlerts.map(a => (
              <div key={a.id} className="flex items-center gap-3 py-2 px-3 rounded-lg border"
                style={{ borderColor: "var(--border-default)" }}>
                <span className="w-2 h-2 rounded-full" style={{ background: a.severity === "critical" ? "#EF4444" : a.severity === "warning" ? "#F59E0B" : "#3B82F6" }} />
                <div className="text-sm flex-1" style={{ color: "var(--text-primary)" }}>{a.title}</div>
                <div className="text-[10px] font-mono uppercase" style={{ color: "var(--text-muted)" }}>{a.type}</div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

const HealthScore = ({ score }) => {
  const color = score >= 80 ? "#10B981" : score >= 50 ? "#F59E0B" : "#EF4444";
  return (
    <div className="flex items-center gap-3 px-4 py-2 surface" data-testid="health-score">
      <div className="relative w-10 h-10">
        <svg viewBox="0 0 36 36" className="w-10 h-10 -rotate-90">
          <circle cx="18" cy="18" r="15" stroke="var(--border-default)" strokeWidth="3" fill="none" />
          <circle cx="18" cy="18" r="15" stroke={color} strokeWidth="3" fill="none"
            strokeDasharray={`${(score / 100) * 94.2} 94.2`} strokeLinecap="round" />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center text-[10px] font-bold" style={{ color }}>{score}</div>
      </div>
      <div>
        <div className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Health Score</div>
        <div className="text-xs font-semibold" style={{ color: "var(--text-primary)" }}>
          {score >= 80 ? "Excellent" : score >= 50 ? "Correct" : "À surveiller"}
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ icon: I, label, value, delta, accent }) => (
  <div className="surface surface-hover p-5" data-testid={`stat-${label}`}>
    <div className="flex items-start justify-between mb-3">
      <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `${accent}15` }}>
        <I className="w-4 h-4" style={{ color: accent }} strokeWidth={2} />
      </div>
      <TrendingUp className="w-3.5 h-3.5" style={{ color: "var(--text-muted)" }} />
    </div>
    <div className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>{label}</div>
    <div className="font-display text-3xl font-bold mt-1" style={{ color: "var(--text-primary)" }}>{value}</div>
    {delta && <div className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>{delta}</div>}
  </div>
);

const QuickAction = ({ icon: I, title, desc, to, color, testid }) => (
  <Link to={to} data-testid={testid} className="surface surface-hover p-5 group block">
    <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3" style={{ background: `${color}15` }}>
      <I className="w-5 h-5" style={{ color }} strokeWidth={2} />
    </div>
    <div className="font-display text-base font-bold" style={{ color: "var(--text-primary)" }}>{title}</div>
    <div className="text-xs mt-1 leading-relaxed" style={{ color: "var(--text-muted)" }}>{desc}</div>
    <div className="mt-3 flex items-center gap-1 text-xs font-semibold transition group-hover:gap-2" style={{ color }}>
      Lancer <ArrowUpRight className="w-3 h-3" />
    </div>
  </Link>
);
